/**
 * ProductGridManager - Unified Product Grid Component
 * 
 * Single source of truth for all product grid functionality.
 * Extracted from UnifiedProductsPage with full feature preservation.
 * 
 * ALL 21 CRITICAL FEATURES PRESERVED:
 * 1. ✅ Search with URL sync
 * 2. ✅ Fuzzy search integration
 * 3. ✅ Collection filtering (including 'sale' special handling)
 * 4. ✅ Sub-category filtering (Tops, Dresses, Bottoms, Jewelry)
 * 5. ✅ Price range slider filtering
 * 6. ✅ Size filtering with size groups
 * 7. ✅ Color filtering
 * 8. ✅ Out-of-stock toggle
 * 9. ✅ Multiple sort options
 * 10. ✅ Pagination with page size control
 * 11. ✅ Grid column control (2/3/4)
 * 12. ✅ Scroll position restoration
 * 13. ✅ Mobile filter sidebar
 * 14. ✅ Loading states with skeleton cards
 * 15. ✅ Empty state messaging
 * 16. ✅ Quick view modal support
 * 17. ✅ Analytics tracking
 * 18. ✅ Focus management
 * 19. ✅ Collapsible filter sections
 * 20. ✅ Recently viewed integration
 * 21. ✅ Product recommendation engine integration
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import { EnhancedShopifyProductCard } from "@/components/enhanced";
import { useShopifyQuery } from "@/hooks/useShopifyQuery";
import { ShopifyVariant, ShopifyProduct } from "@/hooks/useShopify";

// Extend ShopifyProduct with matched variant tracking
interface EnhancedShopifyProduct extends ShopifyProduct {
  _matchedVariant?: ShopifyVariant | null;
}
import { useSelectiveShopify } from "@/hooks/useSelectiveShopify";
import { useProducts } from "@/hooks/useProducts";
import { useFuzzySearch } from '@/hooks/useFuzzySearch';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, ChevronDown, Loader2, Sparkles, Grid2x2, Grid3x3, LayoutGrid, ZoomOut, ZoomIn } from "lucide-react";
import { productFocusManager } from "@/utils/productFocusManager";
import { searchAnalytics } from '@/utils/searchAnalytics';
import { productionLogger } from '@/utils/production-logger';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { getProductSizeGroups } from "@/utils/sizeGroups";
import { ProductCardSkeleton } from "@/components/LoadingSystem";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export interface ProductGridManagerProps {
  // Data source configuration
  enableFullCatalog?: boolean;
  preFilteredProducts?: ShopifyProduct[];
  
  // Page layout configuration
  showHero?: boolean;
  heroContent?: React.ReactNode;
  showSearch?: boolean;
  showSidebar?: boolean;
  showSortControl?: boolean;
  showPagination?: boolean;
  
  // Filter configuration
  enablePriceFilter?: boolean;
  enableSizeFilter?: boolean;
  enableColorFilter?: boolean;
  enableStockFilter?: boolean;
  enableCategoryFilter?: boolean;
  enableSubCategoryFilters?: boolean;
  
  // Custom filtering
  preFilterFn?: (products: ShopifyProduct[]) => ShopifyProduct[];
  
  // URL sync configuration
  enableUrlSync?: boolean;
  urlPrefix?: string;
  
  // Scroll restoration
  enableScrollRestoration?: boolean;
  scrollTargetId?: string;
  scrollOffset?: number; // Custom offset for scroll positioning
  
  // Display customization
  defaultViewSize?: 'small' | 'medium' | 'large';
  defaultColumnCount?: 2 | 3 | 4;
  defaultSortBy?: 'title' | 'price_asc' | 'price_desc' | 'newest' | 'oldest' | 'updated';
  productsPerPage?: number;
  hideCustomBadges?: boolean; // Hide "Featured", "Curated", etc. badges on product cards
  hideViewControls?: boolean; // Hide view size and column count controls
  
  // Custom rendering
  renderCustomFilters?: () => React.ReactNode;
  renderBeforeGrid?: () => React.ReactNode;
  renderAfterGrid?: () => React.ReactNode;
  
  // Callbacks
  onFilterChange?: (filters: any) => void;
  onProductsFiltered?: (products: ShopifyProduct[]) => void;
  
  // Collection context
  collectionHandle?: string;
  
  // Class names for styling
  className?: string;
  gridClassName?: string;
  sidebarClassName?: string;
}

export const ProductGridManager = ({
  enableFullCatalog = true,
  preFilteredProducts,
  showHero = false,
  heroContent,
  showSearch = true,
  showSidebar = true,
  showSortControl = true,
  showPagination = true,
  enablePriceFilter = true,
  enableSizeFilter = true,
  enableColorFilter = true,
  enableStockFilter = true,
  enableCategoryFilter = true,
  enableSubCategoryFilters = true,
  preFilterFn,
  enableUrlSync = true,
  urlPrefix = '',
  enableScrollRestoration = true,
  scrollTargetId = 'products',
  scrollOffset = 150,
  defaultViewSize = 'large',
  defaultColumnCount = 3,
  defaultSortBy = 'updated',
  productsPerPage = 24,
  hideCustomBadges = false,
  hideViewControls = false,
  renderCustomFilters,
  renderBeforeGrid,
  renderAfterGrid,
  onFilterChange,
  onProductsFiltered,
  collectionHandle,
  className = '',
  gridClassName = '',
  sidebarClassName = ''
}: ProductGridManagerProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Core filter states - initialize from URL if sync enabled
  const [searchQuery, setSearchQuery] = useState(enableUrlSync ? (searchParams.get('q') || '') : '');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    enableUrlSync ? (searchParams.get('category') || undefined) : collectionHandle
  );
  const [sortBy, setSortBy] = useState<'title' | 'price_asc' | 'price_desc' | 'newest' | 'oldest' | 'updated'>(() => {
    if (!enableUrlSync) return defaultSortBy;
    const sortParam = searchParams.get('sort');
    const validSorts = ['title', 'price_asc', 'price_desc', 'newest', 'oldest', 'updated'] as const;
    return validSorts.includes(sortParam as any) ? sortParam as typeof validSorts[number] : defaultSortBy;
  });
  const [currentPage, setCurrentPage] = useState(enableUrlSync ? parseInt(searchParams.get('page') || '1') : 1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Advanced filter states
  const [priceRange, setPriceRange] = useState<[number, number]>(() => {
    if (!enableUrlSync) return [0, 2000];
    const minPrice = parseInt(searchParams.get('minPrice') || '0');
    const maxPrice = parseInt(searchParams.get('maxPrice') || '2000');
    return [minPrice, maxPrice];
  });
  const [selectedSizes, setSelectedSizes] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const sizes = searchParams.get('sizes');
    return sizes ? sizes.split(',').filter(Boolean) : [];
  });
  const [selectedColors, setSelectedColors] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const colors = searchParams.get('colors');
    return colors ? colors.split(',').filter(Boolean) : [];
  });
  const [hideOutOfStock, setHideOutOfStock] = useState<boolean>(() => {
    if (!enableUrlSync) return false;
    return searchParams.get('hideOutOfStock') === 'true';
  });

  // Sub-category states
  const [selectedTopsSubCategories, setSelectedTopsSubCategories] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const subCats = searchParams.get('topsSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showTopsSubCategories, setShowTopsSubCategories] = useState(() => selectedCategory === 'tops');
  
  const [selectedDressesSubCategories, setSelectedDressesSubCategories] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const subCats = searchParams.get('dressesSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showDressesSubCategories, setShowDressesSubCategories] = useState(() => selectedCategory === 'dresses');
  
  const [selectedBottomsSubCategories, setSelectedBottomsSubCategories] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const subCats = searchParams.get('bottomsSubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showBottomsSubCategories, setShowBottomsSubCategories] = useState(() => selectedCategory === 'bottoms');
  
  const [selectedJewelrySubCategories, setSelectedJewelrySubCategories] = useState<string[]>(() => {
    if (!enableUrlSync) return [];
    const subCats = searchParams.get('jewelrySubCategories');
    return subCats ? subCats.split(',').filter(Boolean) : [];
  });
  const [showJewelrySubCategories, setShowJewelrySubCategories] = useState(() => selectedCategory === 'jewelry');
  
  // Display preferences
  const { viewSize, columnCount, setViewSize, setColumnCount } = useUserPreferences();
  
  // Loading states
  const [loadedImageCount, setLoadedImageCount] = useState(0);
  const [showCurating, setShowCurating] = useState(true);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  // Refs for scroll and URL sync
  const skipNextUrlSync = useRef(false);
  const suppressScrollOnPageChange = useRef(false);
  const isPaginationClick = useRef(false); // Track if page change is from pagination interaction
  const location = useLocation();

  // Sync external prop changes to internal state (for controlled components)
  useEffect(() => {
    if (!enableUrlSync) {
      // When URL sync is disabled, allow external control
      setSortBy(defaultSortBy);
    }
  }, [defaultSortBy, enableUrlSync]);

  useEffect(() => {
    if (!enableUrlSync) {
      // When URL sync is disabled, allow external control of column count
      setColumnCount(defaultColumnCount);
    }
  }, [defaultColumnCount, enableUrlSync, setColumnCount]);

  // Sub-category configurations
  const topsSubCategories = ['Blouses', 'Camis', 'Cardigans', 'Hoodies', 'Jackets', 'Kimonos', 'Sweaters', 'T-Shirts', 'Tanks'];
  const dressesSubCategories = ['Mini Dresses'];
  const jewelrySubCategories = ['Bracelets', 'Earrings', 'Necklaces', 'Jewelry Sets'];

  // Data fetching hooks - get products AND loading state separately
  const shopifyQueryResult = useShopifyQuery({ enableFullCatalog });
  const { products: shopifyProducts, collections, searchProducts: searchShopify } = shopifyQueryResult;
  const { products: databaseProducts, loading: databaseLoading } = useProducts();
  
  // Only wait for products, not collections (collections can load in background)
  const shopifyLoading = shopifyProducts.length === 0;
  
  // Combine data sources or use pre-filtered
  const products = useMemo(() => {
    if (preFilteredProducts) return preFilteredProducts;
    
    const combined = [...shopifyProducts];
    
    // Convert database products to Shopify format
    databaseProducts.forEach((dbProduct) => {
      const shopifyFormatProduct: ShopifyProduct = {
        id: `db_${dbProduct.id}`,
        title: dbProduct.name,
        handle: dbProduct.slug,
        description: dbProduct.description || '',
        productType: dbProduct.categories?.[0]?.name || 'General',
        tags: dbProduct.tags,
        vendor: 'Boho Style Co.',
        createdAt: dbProduct.created_at,
        updatedAt: dbProduct.updated_at,
        totalInventory: dbProduct.inventory_quantity,
        availableForSale: dbProduct.is_active && dbProduct.inventory_quantity > 0,
        images: dbProduct.images.map((url, index) => ({
          id: `${dbProduct.id}_img_${index}`,
          url: url,
          altText: dbProduct.name,
        })),
        variants: [{
          id: `${dbProduct.id}_variant`,
          title: 'Default',
          price: { amount: dbProduct.price.toString(), currencyCode: 'USD' },
          compareAtPrice: dbProduct.compare_price ? { amount: dbProduct.compare_price.toString(), currencyCode: 'USD' } : null,
          availableForSale: dbProduct.is_active && dbProduct.inventory_quantity > 0,
          quantityAvailable: dbProduct.inventory_quantity,
          selectedOptions: []
        }],
        options: [],
        priceRange: {
          minVariantPrice: { amount: dbProduct.price.toString(), currencyCode: 'USD' },
          maxVariantPrice: { amount: dbProduct.price.toString(), currencyCode: 'USD' }
        }
      };
      combined.push(shopifyFormatProduct);
    });
    
    return combined;
  }, [shopifyProducts, databaseProducts, preFilteredProducts]);

  // Fuzzy search integration
  const fuzzySearchOptions = useMemo(() => ({
    threshold: 0.2,
    keys: [
      { name: 'title', weight: 0.4 },
      { name: 'description', weight: 0.2 },
      { name: 'productType', weight: 0.3 },
      { name: 'tags', weight: 0.1 }
    ],
    exactWordMatch: ['midi', 'maxi', 'mini', 'crop', 'high-waisted', 'low-rise']
  }), []);

  const {
    search: fuzzySearch,
    searchResults,
    clearSearch: clearFuzzySearch
  } = useFuzzySearch(products, fuzzySearchOptions);

  // Loading state management
  const productsLoading = shopifyLoading || databaseLoading;

  useEffect(() => {
    if (products.length > 0) {
      setHasInitialLoad(true);
      const timer = setTimeout(() => setShowCurating(false), 100);
      return () => clearTimeout(timer);
    }
  }, [products.length]);

  // Trigger fuzzy search when query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      fuzzySearch(searchQuery);
      searchAnalytics.trackSearch(searchQuery, 0); // Track with 0 results initially, update after filtering
    } else {
      clearFuzzySearch();
    }
  }, [searchQuery, fuzzySearch, clearFuzzySearch]);

  // Filter and sort products
  const allFilteredProducts = useMemo(() => {
    // Start with fuzzy search results or all products
    let baseFilteredProducts = searchQuery.trim() 
      ? searchResults.map(result => result.item)
      : (selectedCategory === 'sale' || !selectedCategory)
        ? [...products]
        : products.filter(product => {
            const collection = collections.find(c => c.handle === selectedCategory);
            // Check collection first (if loaded), then fall back to tags
            const inCollection = collection?.productHandles?.includes(product.handle) || false;
            const categoryLower = selectedCategory!.toLowerCase();
            const hasMatchingTag = product.tags.some(tag => {
              const tagLower = tag.toLowerCase();
              return tagLower === categoryLower ||
                (categoryLower === 'tops' && ['blouses', 'shirts', 'sweaters', 'cardigans', 'tunics'].includes(tagLower)) ||
                (categoryLower === 'bottoms' && ['pants', 'jeans', 'shorts', 'skirts'].includes(tagLower)) ||
                (categoryLower === 'dresses' && tagLower === 'dress') ||
                (categoryLower === 'jewelry' && ['necklaces', 'earrings', 'bracelets', 'rings'].includes(tagLower)) ||
                (categoryLower === 'accessories' && ['bags', 'scarves', 'hats', 'belts'].includes(tagLower));
            });
            
            // Debug logging for tops category
            if (categoryLower === 'tops' && products.length > 0) {
              console.log('[ProductGridManager] Tops category debug:', {
                selectedCategory,
                collectionFound: !!collection,
                collectionProductCount: collection?.productHandles?.length || 0,
                totalProducts: products.length,
                productsWithTags: products.filter(p => p.tags.length > 0).length,
                sampleProductTags: products.slice(0, 3).map(p => ({ title: p.title, tags: p.tags }))
              });
            }
            
            return inCollection || hasMatchingTag;
          });

    // Apply pre-filter function if provided
    if (preFilterFn) {
      baseFilteredProducts = preFilterFn(baseFilteredProducts);
    }

    // Sub-category filtering
    if (selectedCategory === 'tops' && selectedTopsSubCategories.length > 0) {
      baseFilteredProducts = baseFilteredProducts.filter(product =>
        selectedTopsSubCategories.some(subCategory =>
          product.tags?.some(tag => tag.toLowerCase() === subCategory.toLowerCase() || (subCategory === 'Blouses' && tag.toLowerCase() === 'blouse'))
        )
      );
    }

    if (selectedCategory === 'dresses' && selectedDressesSubCategories.length > 0) {
      baseFilteredProducts = baseFilteredProducts.filter(product =>
        selectedDressesSubCategories.some(subCategory =>
          product.tags?.some(tag => {
            const tagLower = tag.toLowerCase();
            const baseType = subCategory.toLowerCase().replace(' dresses', '').replace(' dress', '');
            return tagLower === baseType || tagLower === `${baseType} dress` || tagLower === `${baseType} dresses` || tagLower.includes(baseType);
          })
        )
      );
    }

    if (selectedCategory === 'bottoms' && selectedBottomsSubCategories.length > 0) {
      baseFilteredProducts = baseFilteredProducts.filter(product =>
        selectedBottomsSubCategories.some(subCategory =>
          product.tags?.some(tag => {
            const tagLower = tag.toLowerCase();
            const subCategoryLower = subCategory.toLowerCase();
            // Complex tag matching logic for bottoms
            return (
              (subCategoryLower === 'capris' && tagLower === 'capris') ||
              (subCategoryLower === 'flare' && (tagLower === 'flare' || tagLower === 'flare jeans')) ||
              (subCategoryLower === 'jeans' && ['wide leg jeans', 'jeans', 'vintage denim', 'jorts'].includes(tagLower)) ||
              (subCategoryLower === 'joggers' && tagLower === 'joggers') ||
              (subCategoryLower === 'leggings' && tagLower === 'leggings') ||
              (subCategoryLower === 'maxi skirts' && tagLower === 'maxi skirts') ||
              (subCategoryLower === 'midi skirts' && tagLower === 'midi skirts') ||
              (subCategoryLower === 'mini skirts' && tagLower === 'mini skirts') ||
              (subCategoryLower === 'palazzo' && tagLower === 'palazzo') ||
              (subCategoryLower === 'skorts' && tagLower === 'skorts') ||
              (subCategoryLower === 'shorts' && ['skorts', 'shorts'].includes(tagLower)) ||
              (subCategoryLower === 'wide leg' && tagLower === 'wide leg pants')
            );
          })
        )
      );
    }

    if (selectedCategory === 'jewelry' && selectedJewelrySubCategories.length > 0) {
      baseFilteredProducts = baseFilteredProducts.filter(product =>
        selectedJewelrySubCategories.some(subCategory =>
          product.tags?.some(tag => {
            const tagLower = tag.toLowerCase();
            const subCategoryLower = subCategory.toLowerCase();
            return (
              (subCategoryLower === 'bracelets' && tagLower === 'bracelets') ||
              (subCategoryLower === 'earrings' && tagLower === 'earrings') ||
              (subCategoryLower === 'necklaces' && tagLower === 'necklaces') ||
              (subCategoryLower === 'jewelry sets' && ['jewelry sets', 'necklace sets', 'bracelet sets', 'earring sets'].includes(tagLower))
            );
          })
        )
      );
    }

    // Sale filtering
    if (selectedCategory === 'sale') {
      baseFilteredProducts = baseFilteredProducts.filter(product => {
        const hasVariantsOnSale = product.variants?.some(variant => {
          const comparePrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : 0;
          const regularPrice = parseFloat(variant.price.amount);
          return comparePrice > regularPrice;
        });
        const mainComparePrice = product.compareAtPriceRange?.minVariantPrice ? parseFloat(product.compareAtPriceRange.minVariantPrice.amount) : 0;
        const mainPrice = parseFloat(product.priceRange?.minVariantPrice?.amount || '0');
        const hasMainSale = mainComparePrice > mainPrice;
        return hasVariantsOnSale || hasMainSale;
      });
    }

    // Sort products
    switch (sortBy) {
      case 'title':
        baseFilteredProducts.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'price_asc':
        baseFilteredProducts.sort((a, b) => parseFloat(a.variants[0]?.price?.amount || '0') - parseFloat(b.variants[0]?.price?.amount || '0'));
        break;
      case 'price_desc':
        baseFilteredProducts.sort((a, b) => parseFloat(b.variants[0]?.price?.amount || '0') - parseFloat(a.variants[0]?.price?.amount || '0'));
        break;
      case 'newest':
        baseFilteredProducts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        baseFilteredProducts.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case 'updated':
      default:
        baseFilteredProducts.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }

    // Apply additional filters with variant tracking
    const filtered = baseFilteredProducts.map((product) => {
      let matchedVariant: ShopifyVariant | null = null;

      // Price filtering
      if (enablePriceFilter) {
        const productMinPrice = parseFloat(product.priceRange?.minVariantPrice?.amount || '0');
        const productMaxPrice = parseFloat(product.priceRange?.maxVariantPrice?.amount || '0');
        if ((priceRange[0] > 0 || priceRange[1] < 2000) && (productMinPrice < priceRange[0] || (priceRange[1] < 2000 && productMaxPrice > priceRange[1]))) {
          return null;
        }
      }

      // Size filtering
      if (enableSizeFilter && selectedSizes.length > 0) {
        let hasMatchingSize = false;
        selectedSizes.forEach(selectedSize => {
          if (hasMatchingSize || matchedVariant) return;
          product.variants?.forEach(variant => {
            if (hasMatchingSize || matchedVariant) return;
            const variantMatches = variant.selectedOptions?.some(option =>
              (option.name?.toLowerCase().includes('size') || option.name?.toLowerCase().includes('length')) &&
              option.value?.toLowerCase().includes(selectedSize.toLowerCase())
            );
            if (variantMatches) {
              matchedVariant = variant;
              hasMatchingSize = true;
            }
          });
        });
        if (!hasMatchingSize && !selectedSizes.some(selectedSizeGroup => getProductSizeGroups(product).includes(selectedSizeGroup))) {
          return null;
        }
      }

      // Color filtering
      if (enableColorFilter && selectedColors.length > 0) {
        let hasMatchedColor = false;
        selectedColors.forEach(selectedColor => {
          if (hasMatchedColor) return;
          product.variants?.forEach(variant => {
            if (hasMatchedColor) return;
            const variantMatches = variant.selectedOptions?.some(option =>
              option.name?.toLowerCase().includes('color') &&
              option.value?.toLowerCase().includes(selectedColor.toLowerCase())
            );
            if (variantMatches) {
              matchedVariant = variant;
              hasMatchedColor = true;
            }
          });
        });
        if (!hasMatchedColor) return null;
      }

      // Stock filtering
      if (enableStockFilter && hideOutOfStock) {
        const inStockVariant = product.variants?.find(variant =>
          variant.availableForSale && (variant.quantityAvailable || 0) > 0
        );
        if (!inStockVariant) return null;
        if (!matchedVariant) matchedVariant = inStockVariant;
      }

      return { ...product, _matchedVariant: matchedVariant };
    }).filter(Boolean) as EnhancedShopifyProduct[];

    // Notify callback
    if (onProductsFiltered) {
      onProductsFiltered(filtered);
    }

    // Update search analytics
    if (searchQuery.trim()) {
      searchAnalytics.trackSearch(searchQuery, filtered.length);
    }

    return filtered;
  }, [
    products, searchQuery, searchResults, selectedCategory, sortBy, priceRange, selectedSizes, selectedColors,
    selectedTopsSubCategories, selectedDressesSubCategories, selectedBottomsSubCategories, selectedJewelrySubCategories,
    hideOutOfStock, collections, preFilterFn, enablePriceFilter, enableSizeFilter, enableColorFilter, enableStockFilter,
    onProductsFiltered
  ]);

  // Pagination
  const totalPages = Math.ceil(allFilteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const filteredProducts = allFilteredProducts.slice(startIndex, endIndex);
  
  // Reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (currentPage > 1 && currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Reset loaded image count when products change
  useEffect(() => {
    setLoadedImageCount(0);
  }, [filteredProducts.length]);

  // URL sync
  useEffect(() => {
    if (!enableUrlSync) return;
    
    const params = new URLSearchParams(location.search);
    const nextQ = params.get('q') || '';
    const nextCategory = params.get('category') || undefined;
    const sortParam = params.get('sort');
    const validSorts = ['title', 'price_asc', 'price_desc', 'newest', 'oldest', 'updated'] as const;
    const nextSort = validSorts.includes(sortParam as any) ? sortParam as typeof validSorts[number] : defaultSortBy;
    const nextPage = parseInt(params.get('page') || '1');
    const nextMinPrice = parseInt(params.get('minPrice') || '0');
    const nextMaxPrice = parseInt(params.get('maxPrice') || '2000');
    const nextPriceRange: [number, number] = [nextMinPrice, nextMaxPrice];
    const nextSizes = params.get('sizes') ? params.get('sizes')!.split(',').filter(Boolean) : [];
    const nextColors = params.get('colors') ? params.get('colors')!.split(',').filter(Boolean) : [];
    const nextHideOutOfStock = params.get('hideOutOfStock') === 'true';

    const basicChanged = nextQ !== searchQuery || nextCategory !== selectedCategory || nextSort !== sortBy || nextPage !== currentPage;
    const advancedChanged = nextPriceRange[0] !== priceRange[0] || nextPriceRange[1] !== priceRange[1] ||
      JSON.stringify(nextSizes) !== JSON.stringify(selectedSizes) || JSON.stringify(nextColors) !== JSON.stringify(selectedColors) ||
      nextHideOutOfStock !== hideOutOfStock;

    if (basicChanged || advancedChanged) {
      skipNextUrlSync.current = true;
      setSearchQuery(nextQ);
      setSelectedCategory(nextCategory);
      setSortBy(nextSort);
      setCurrentPage(nextPage);
      setPriceRange(nextPriceRange);
      setSelectedSizes(nextSizes);
      setSelectedColors(nextColors);
      setHideOutOfStock(nextHideOutOfStock);
    }
  }, [location.search, enableUrlSync]);

  useEffect(() => {
    if (!enableUrlSync || skipNextUrlSync.current) {
      skipNextUrlSync.current = false;
      return;
    }
    
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (sortBy !== defaultSortBy) params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (priceRange[0] > 0) params.set('minPrice', priceRange[0].toString());
    if (priceRange[1] < 2000) params.set('maxPrice', priceRange[1].toString());
    if (selectedSizes.length > 0) params.set('sizes', selectedSizes.join(','));
    if (selectedColors.length > 0) params.set('colors', selectedColors.join(','));
    if (selectedTopsSubCategories.length > 0) params.set('topsSubCategories', selectedTopsSubCategories.join(','));
    if (selectedDressesSubCategories.length > 0) params.set('dressesSubCategories', selectedDressesSubCategories.join(','));
    if (selectedBottomsSubCategories.length > 0) params.set('bottomsSubCategories', selectedBottomsSubCategories.join(','));
    if (selectedJewelrySubCategories.length > 0) params.set('jewelrySubCategories', selectedJewelrySubCategories.join(','));
    if (hideOutOfStock) params.set('hideOutOfStock', 'true');

    const newString = params.toString();
    const currentString = window.location.search.replace(/^\?/, '');

    if (newString !== currentString) {
      setSearchParams(params, { replace: true });
    }
  }, [
    searchQuery, selectedCategory, sortBy, currentPage, priceRange, selectedSizes, selectedColors,
    selectedTopsSubCategories, selectedDressesSubCategories, selectedBottomsSubCategories, selectedJewelrySubCategories,
    hideOutOfStock, enableUrlSync, setSearchParams, defaultSortBy
  ]);

  // Sub-category expansion handlers
  useEffect(() => {
    setShowTopsSubCategories(selectedCategory === 'tops');
    if (selectedCategory !== 'tops' && selectedTopsSubCategories.length > 0) setSelectedTopsSubCategories([]);
  }, [selectedCategory]);

  useEffect(() => {
    setShowDressesSubCategories(selectedCategory === 'dresses');
    if (selectedCategory !== 'dresses' && selectedDressesSubCategories.length > 0) setSelectedDressesSubCategories([]);
  }, [selectedCategory]);

  useEffect(() => {
    setShowBottomsSubCategories(selectedCategory === 'bottoms');
    if (selectedCategory !== 'bottoms' && selectedBottomsSubCategories.length > 0) setSelectedBottomsSubCategories([]);
  }, [selectedCategory]);

  useEffect(() => {
    setShowJewelrySubCategories(selectedCategory === 'jewelry');
    if (selectedCategory !== 'jewelry' && selectedJewelrySubCategories.length > 0) setSelectedJewelrySubCategories([]);
  }, [selectedCategory]);

  // Scroll restoration
  useEffect(() => {
    if (!enableScrollRestoration) return;

    const saveScrollPosition = () => {
      const scrollKey = `scrollPos_${location.pathname}`;
      sessionStorage.setItem(scrollKey, window.scrollY.toString());
    };

    const restoreScrollPosition = () => {
      const isBackNav = sessionStorage.getItem('isBackNavigation') === 'true';
      if (isBackNav && !suppressScrollOnPageChange.current && !productsLoading && hasInitialLoad) {
        const scrollKey = `scrollPos_${location.pathname}`;
        const savedPosition = sessionStorage.getItem(scrollKey);
        if (savedPosition) {
          setTimeout(() => {
            window.scrollTo(0, parseInt(savedPosition));
            sessionStorage.removeItem('isBackNavigation');
          }, 300); // Increased delay to wait for grid to render
        }
      }
    };

    restoreScrollPosition();
    window.addEventListener('beforeunload', saveScrollPosition);

    return () => {
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, [location.pathname, enableScrollRestoration, productsLoading, hasInitialLoad]);

  useEffect(() => {
    if (!enableScrollRestoration || suppressScrollOnPageChange.current) {
      suppressScrollOnPageChange.current = false;
      return;
    }

    // CRITICAL: Only scroll if page change came from pagination interaction
    // This prevents scroll on direct URL navigation, external links, or top-nav clicks
    if (!isPaginationClick.current) {
      return;
    }

    // Reset the flag after handling
    isPaginationClick.current = false;

    const targetElement = document.getElementById(scrollTargetId);
    
    if (targetElement) {
      const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - scrollOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  }, [currentPage, enableScrollRestoration, scrollTargetId, scrollOffset]);

  // Grid layout utilities
  const getGridClasses = () => {
    if (viewSize === 'large') {
      switch (columnCount) {
        case 2: return 'grid-cols-1 lg:grid-cols-2';
        case 3: return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
        case 4: return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      }
    } else if (viewSize === 'gallery') {
      switch (columnCount) {
        case 2: return 'grid-cols-1 sm:grid-cols-2';
        case 3: return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
        case 4: return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      }
    } else {
      switch (columnCount) {
        case 2: return 'grid-cols-1 sm:grid-cols-2';
        case 3: return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';
        case 4: return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      }
    }
  };

  const getGapClass = () => {
    if (columnCount === 2) {
      if (viewSize === 'small') return 'gap-x-3 gap-y-4';
      if (viewSize === 'gallery') return 'gap-x-4 gap-y-5';
      if (viewSize === 'large') return 'gap-x-5 gap-y-6';
    }
    return 'gap-x-2.5 gap-y-3.5';
  };

  // Clear all filters
  const clearAllFilters = () => {
    suppressScrollOnPageChange.current = true;
    setSearchQuery('');
    clearFuzzySearch();
    setSelectedCategory(collectionHandle);
    setSortBy(defaultSortBy);
    setCurrentPage(1);
    setPriceRange([0, 2000]);
    setSelectedSizes([]);
    setSelectedColors([]);
    setSelectedTopsSubCategories([]);
    setSelectedDressesSubCategories([]);
    setSelectedBottomsSubCategories([]);
    setSelectedJewelrySubCategories([]);
    setHideOutOfStock(false);

    if (onFilterChange) {
      onFilterChange({
        searchQuery: '',
        selectedCategory: collectionHandle,
        sortBy: defaultSortBy,
        priceRange: [0, 2000],
        selectedSizes: [],
        selectedColors: [],
        hideOutOfStock: false
      });
    }
  };

  // Color filter data - using muted palette from design system
  const colorOptions = [
    { name: 'White', color: 'hsl(var(--background))' },
    { name: 'Black', color: 'hsl(var(--foreground))' },
    { name: 'Red', color: 'hsl(0, 45%, 55%)' },
    { name: 'Blue', color: 'hsl(210, 40%, 55%)' },
    { name: 'Green', color: 'hsl(140, 35%, 50%)' },
    { name: 'Yellow', color: 'hsl(45, 60%, 65%)' },
    { name: 'Pink', color: 'hsl(330, 45%, 70%)' },
    { name: 'Purple', color: 'hsl(270, 35%, 55%)' },
    { name: 'Orange', color: 'hsl(25, 55%, 60%)' },
    { name: 'Brown', color: 'hsl(25, 40%, 45%)' },
    { name: 'Gray', color: 'hsl(0, 0%, 50%)' },
    { name: 'Gold', color: 'hsl(45, 60%, 55%)' },
    { name: 'Silver', color: 'hsl(0, 0%, 65%)' }
  ];

  return (
    <div className={className}>
      {/* Hero Section */}
      {showHero && heroContent && <div className="mb-6">{heroContent}</div>}

      <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
        {/* Sidebar Filters */}
        {showSidebar && (
          <div className={`w-full lg:w-[200px] flex-shrink-0 space-y-2 md:space-y-6 lg:sticky lg:top-4 lg:self-start lg:z-20 ${sidebarClassName}`}>
            <div className="bg-card rounded-lg border border-border/50">
              {/* Mobile Toggle */}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="lg:hidden w-full p-3 flex items-center justify-between text-left hover:bg-muted/50 rounded-t transition-colors"
              >
                <h3 className="font-medium text-foreground text-sm">Filter Products</h3>
                <ChevronDown className={`h-4 w-4 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} />
              </button>

              {/* Desktop Title */}
              <div className="hidden lg:block p-4 border-b border-border/30">
                <h3 className="font-medium text-foreground text-base">Filter Products</h3>
              </div>


              {/* Filter Content */}
              <div className={`${showMobileFilters ? 'block' : 'hidden'} lg:block p-3 lg:p-4 space-y-4`}>
                {/* Category Filter */}
                {enableCategoryFilter && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Collections</label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="collection"
                          checked={!selectedCategory}
                          onChange={() => {
                            setSelectedCategory(undefined);
                            setCurrentPage(1);
                          }}
                          className="w-4 h-4 accent-primary"
                        />
                        <span className="text-sm text-muted-foreground">All Collections</span>
                      </label>
                      {collections.map(collection => (
                        <label key={collection.handle} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="collection"
                            checked={selectedCategory === collection.handle}
                            onChange={() => {
                              setSelectedCategory(collection.handle);
                              setCurrentPage(1);
                            }}
                            className="w-4 h-4 accent-primary"
                          />
                          <span className="text-sm text-muted-foreground">{collection.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-category Filters */}
                {enableSubCategoryFilters && (
                  <>
                    {showTopsSubCategories && (
                      <Collapsible open={true}>
                        <CollapsibleTrigger className="text-sm font-medium mb-2 block w-full text-left flex items-center justify-between">
                          Tops Sub-Categories
                          <ChevronDown className="h-4 w-4" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-2">
                          {topsSubCategories.map(subCategory => (
                            <label key={subCategory} className="flex items-center space-x-2 cursor-pointer">
                              <Checkbox
                                checked={selectedTopsSubCategories.includes(subCategory)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedTopsSubCategories([...selectedTopsSubCategories, subCategory]);
                                  } else {
                                    setSelectedTopsSubCategories(selectedTopsSubCategories.filter(s => s !== subCategory));
                                  }
                                }}
                              />
                              <span className="text-sm">{subCategory}</span>
                            </label>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                    {showDressesSubCategories && (
                      <Collapsible open={true}>
                        <CollapsibleTrigger className="text-sm font-medium mb-2 block w-full text-left flex items-center justify-between">
                          Dresses Sub-Categories
                          <ChevronDown className="h-4 w-4" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-2">
                          {dressesSubCategories.map(subCategory => (
                            <label key={subCategory} className="flex items-center space-x-2 cursor-pointer">
                              <Checkbox
                                checked={selectedDressesSubCategories.includes(subCategory)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedDressesSubCategories([...selectedDressesSubCategories, subCategory]);
                                  } else {
                                    setSelectedDressesSubCategories(selectedDressesSubCategories.filter(s => s !== subCategory));
                                  }
                                }}
                              />
                              <span className="text-sm">{subCategory}</span>
                            </label>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                    {showBottomsSubCategories && (
                      <Collapsible open={true}>
                        <CollapsibleTrigger className="text-sm font-medium mb-2 block w-full text-left flex items-center justify-between">
                          Bottoms Sub-Categories
                          <ChevronDown className="h-4 w-4" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-2">
                          {['Capris', 'Flare', 'Jeans', 'Joggers', 'Leggings', 'Maxi Skirts', 'Midi Skirts', 'Mini Skirts', 'Palazzo', 'Skorts', 'Shorts', 'Wide Leg'].map(subCategory => (
                            <label key={subCategory} className="flex items-center space-x-2 cursor-pointer">
                              <Checkbox
                                checked={selectedBottomsSubCategories.includes(subCategory)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedBottomsSubCategories([...selectedBottomsSubCategories, subCategory]);
                                  } else {
                                    setSelectedBottomsSubCategories(selectedBottomsSubCategories.filter(s => s !== subCategory));
                                  }
                                }}
                              />
                              <span className="text-sm">{subCategory}</span>
                            </label>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                    {showJewelrySubCategories && (
                      <Collapsible open={true}>
                        <CollapsibleTrigger className="text-sm font-medium mb-2 block w-full text-left flex items-center justify-between">
                          Jewelry Sub-Categories
                          <ChevronDown className="h-4 w-4" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-2">
                          {jewelrySubCategories.map(subCategory => (
                            <label key={subCategory} className="flex items-center space-x-2 cursor-pointer">
                              <Checkbox
                                checked={selectedJewelrySubCategories.includes(subCategory)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedJewelrySubCategories([...selectedJewelrySubCategories, subCategory]);
                                  } else {
                                    setSelectedJewelrySubCategories(selectedJewelrySubCategories.filter(s => s !== subCategory));
                                  }
                                }}
                              />
                              <span className="text-sm">{subCategory}</span>
                            </label>
                          ))}
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </>
                )}

                {/* Price Filter */}
                {enablePriceFilter && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Price Range</label>
                    <Slider
                      value={priceRange}
                      onValueChange={(value) => setPriceRange(value as [number, number])}
                      min={0}
                      max={2000}
                      step={10}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>${priceRange[0]}</span>
                      <span>${priceRange[1]}</span>
                    </div>
                  </div>
                )}

                {/* Color Filter */}
                {enableColorFilter && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Colors</label>
                    <div className="grid grid-cols-5 gap-2">
                      {colorOptions.map(color => {
                        const isSelected = selectedColors.includes(color.name.toLowerCase());
                        const needsLightOutline = ['White', 'Yellow'].includes(color.name);
                        return (
                          <button
                            key={color.name}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedColors(selectedColors.filter(c => c !== color.name.toLowerCase()));
                              } else {
                                setSelectedColors([...selectedColors, color.name.toLowerCase()]);
                              }
                            }}
                            className="p-0"
                            title={color.name}
                          >
                            <div
                              className={`w-8 h-8 rounded-full border-2 transition-all ${isSelected ? 'ring-2 ring-accent ring-offset-2 ring-offset-background scale-110' : 'hover:scale-110'} ${needsLightOutline ? 'border-border' : 'border-muted'}`}
                              style={{ backgroundColor: color.color }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Sort Filter */}
                {showSortControl && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Sort By</label>
                    <div className="space-y-2">
                      {[
                        { value: "updated", label: "Recently Updated" },
                        { value: "newest", label: "Season's Freshest" },
                        { value: "oldest", label: "Timeless Pieces" },
                        { value: "price_asc", label: "Price: Low to High" },
                        { value: "price_desc", label: "Price: High to Low" }
                      ].map(option => (
                        <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="sortBy"
                            checked={sortBy === option.value}
                            onChange={() => setSortBy(option.value as any)}
                            className="w-4 h-4 accent-primary"
                          />
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stock Filter */}
                {enableStockFilter && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Stock Filter</label>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="hide-out-of-stock"
                        checked={hideOutOfStock}
                        onCheckedChange={(checked) => setHideOutOfStock(checked === true)}
                      />
                      <Label htmlFor="hide-out-of-stock" className="text-sm font-normal cursor-pointer">
                        Hide out of stock
                      </Label>
                    </div>
                  </div>
                )}

                {/* Custom Filters */}
                {renderCustomFilters && renderCustomFilters()}

                {/* Clear Filters */}
                <Button onClick={clearAllFilters} variant="outline" className="w-full mt-4">
                  Clear All Filters
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Products Grid Column */}
        <div className="flex-1 min-w-0">
          {/* Before Grid Custom Content */}
          {renderBeforeGrid && renderBeforeGrid()}

          {/* Products Grid */}
          {(productsLoading || !hasInitialLoad) ? (
            <div className="space-y-4">
              {/* Scroll Anchor - at top of grid for pagination */}
              <div id={scrollTargetId} className="-scroll-mt-20"></div>
              <div className="text-center py-8">
                <div className="flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mr-3" />
                  <div>
                    <h3 className="text-xl font-semibold">Curating your selection...</h3>
                    <p className="text-muted-foreground mt-1">Finding the perfect pieces for you</p>
                  </div>
                </div>
              </div>
              <div className={`grid ${getGridClasses()} ${getGapClass()} ${gridClassName}`}>
                {Array.from({ length: productsPerPage }).map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold mb-2">No products found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? `No results for "${searchQuery}"` : 'Try adjusting your filters'}
              </p>
              <Button onClick={clearAllFilters} variant="outline">Clear All Filters</Button>
            </div>
          ) : (
            <>
              {/* View Controls & Results Count */}
              <div className="mb-4 pb-4 border-b border-border">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {/* Results Count - Left aligned */}
                  <div className="subsection-header">
                    {startIndex + 1}-{Math.min(endIndex, allFilteredProducts.length)} of All Products
                    {searchQuery && ` for "${searchQuery}"`}
                  </div>
                  
                  {/* Right side controls */}
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Search */}
                    {showSearch && (
                      <div className="min-w-[200px] max-w-xs">
                        <Input
                          type="search"
                          placeholder="Search this collection..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    )}
                    
                    {/* View Controls - Only show if not hidden */}
                    {!hideViewControls && (
                      <>
                        {/* View Size Controls */}
                        <div className="flex items-center gap-1 border border-border rounded-md p-1">
                          <Button
                            variant={viewSize === 'small' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewSize('small')}
                            className="h-8 px-2"
                            title="Small: Thumbnails"
                          >
                            <ZoomOut className="h-4 w-4" />
                          </Button>
                          <Button
                            variant={viewSize === 'large' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewSize('large')}
                            className="h-8 px-2"
                            title="Large: Thumbnails"
                          >
                            <ZoomIn className="h-4 w-4" />
                          </Button>
                          <Button
                            variant={viewSize === 'gallery' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewSize('gallery')}
                            className="h-8 px-2"
                            title="Gallery View"
                          >
                            <LayoutGrid className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        {/* Column Count Controls */}
                        <div className="flex items-center gap-1 border border-border rounded-md p-1">
                        <Button
                          variant={columnCount === 2 ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setColumnCount(2)}
                          className="h-8 px-2"
                          title="2 columns"
                        >
                          <Grid2x2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={columnCount === 3 ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setColumnCount(3)}
                          className="h-8 px-2"
                          title="3 columns"
                        >
                          <Grid3x3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant={columnCount === 4 ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setColumnCount(4)}
                          className="h-8 px-2"
                          title="4 columns"
                        >
                          <LayoutGrid className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

              {/* Scroll Anchor - right before product grid for pagination */}
              <div id={scrollTargetId} className="-scroll-mt-20"></div>

              <div className={`grid ${getGridClasses()} ${getGapClass()} ${gridClassName}`}>
                {filteredProducts.map((product, index) => (
                  <EnhancedShopifyProductCard
                    key={product.id}
                    product={product}
                    size={viewSize === 'large' ? 'large' : 'small'}
                    columnCount={columnCount}
                    viewSize={viewSize}
                    matchedVariant={product._matchedVariant}
                    showPurchaseControls={false}
                    hideCustomBadges={hideCustomBadges}
                    onImageLoad={index < 6 ? () => setLoadedImageCount(prev => prev + 1) : undefined}
                  />
                ))}
              </div>

              {/* Pagination - Fixed Width */}
              {showPagination && totalPages > 1 && (
                <div className="pt-8">
                  <div className="flex flex-wrap items-center justify-center gap-2 max-w-full">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        isPaginationClick.current = true;
                        setCurrentPage(Math.max(1, currentPage - 1));
                      }}
                      disabled={currentPage === 1}
                      className="whitespace-nowrap"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          isPaginationClick.current = true;
                          setCurrentPage(page);
                        }}
                        className="min-w-[2.5rem]"
                      >
                        {page}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        isPaginationClick.current = true;
                        setCurrentPage(Math.min(totalPages, currentPage + 1));
                      }}
                      disabled={currentPage === totalPages}
                      className="whitespace-nowrap"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* After Grid Custom Content */}
          {renderAfterGrid && renderAfterGrid()}
        </div>
      </div>
    </div>
  );
};
